import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

export type NotificationChannel = 'push' | 'banner' | 'inApp' | 'email';
export type NotificationStatus = 'live' | 'dryRun' | 'planned' | 'awaitingCopy';
export type RetentionMode = 'off' | 'dryRun' | 'live';

export interface NotificationButton {
    label: string;
    route: string;
    url: string;
}

/** Mirrors NotificationCatalogEntry in PeakPWA/functions/src/notificationCatalog.ts. */
export interface PushNotificationEntry {
    id: string;
    name: string;
    group: string;
    row?: string;
    category?: string;
    trigger: string;
    conditions: string[];
    channels: NotificationChannel[];
    title?: string;
    body?: string;
    buttons?: NotificationButton[];
    buttonsIfTrialUsed?: NotificationButton[];
    priority?: number;
    countsTowardsLimit: boolean;
    userSetting?: string;
    status: NotificationStatus;
    source: string;
    notes?: string[];
    sent7d: number;
    sent30d: number;
    dryRun7d: number;
    /** Retention rows: who the row is switched on for (code or settings document), null when off. */
    liveFor: 'everyone' | 'testAccounts' | null;
}

interface CatalogResponse {
    retentionMode: RetentionMode;
    testAccountCount: number;
    categories: Record<string, string>;
    notifications: PushNotificationEntry[];
}

export type RowStatus = 'off' | 'notMet' | 'skipped' | 'blocked' | 'lowerPriority' | 'picked';
export type PickBlock = 'pausedAfterInactivity' | 'dailyLimit' | 'weeklyLimit' | 'alreadyPickedToday';
export type AccountExclusion = 'noProfile' | 'demo' | 'coach' | 'organisationStaff' | 'employee';

export interface PreviewChannel {
    picked: string | null;
    blockedBy: PickBlock | null;
    rows: { row: string; status: RowStatus; conditionMet: boolean; because?: string }[];
    message: { title: string; body: string; buttons: string[] } | null;
}

/** Mirrors RetentionPreview in PeakPWA/functions/src/retention.ts. */
export interface RetentionPreview {
    uid: string;
    mode: RetentionMode;
    testAccount: boolean;
    exclusion: AccountExclusion | null;
    receives: boolean;
    rowsOn: string[];
    summaryStored: boolean;
    timezone: string | null;
    localDate?: string;
    localHour?: number;
    todaysRow?: string | null;
    banner?: PreviewChannel;
    push?: PreviewChannel & { pushBlockedBy: 'noDevice' | 'switchedOff' | null };
    limits?: { sentToday: boolean; sentThisWeek: number; weeklyLimit: number; pausedAfterInactivity: boolean };
    currentBanners?: { slot: 'eventBanner' | 'dailyBanner'; row: string; day: string; delivered: boolean; goalMet: boolean }[];
    facts?: Record<string, unknown>;
}

/** Super admins only: which retention message a user (ID or email) would get today and why. */
export async function fetchRetentionPreview(user: string): Promise<RetentionPreview> {
    const fn = httpsCallable<{ user: string }, RetentionPreview>(functions, 'previewRetentionNotification');
    return (await fn({ user })).data;
}

export function usePushNotifications() {
    const [notifications, setNotifications] = useState<PushNotificationEntry[]>([]);
    const [categories, setCategories] = useState<Record<string, string>>({});
    const [retentionMode, setRetentionMode] = useState<RetentionMode>('off');
    const [testAccountCount, setTestAccountCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchCatalog = useCallback(async () => {
        setLoading(true);
        try {
            const fn = httpsCallable<void, CatalogResponse>(functions, 'getNotificationCatalog');
            const { data } = await fn();
            setNotifications(data.notifications);
            setCategories(data.categories);
            setRetentionMode(data.retentionMode);
            setTestAccountCount(data.testAccountCount ?? 0);
            setError(null);
        } catch (err) {
            console.error('[PUSH_NOTIFICATIONS_FETCH] Error:', err);
            setError(err as Error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCatalog();
    }, [fetchCatalog]);

    return { notifications, categories, retentionMode, testAccountCount, loading, error, fetchCatalog };
}
